import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import BopBackLink from '../../components/BopBackLink';
import Layout from '../../components/Layout';
import Modal from '../../components/Modal';
import ApiInspector from '../../components/ApiInspector';
import PrintModal from '../../components/PrintModal';
import BopRowActions, { RowIcon } from '../../components/BopRowActions';
import { apiFetch } from '../../lib/api';
import { usePermesso } from '../../context/PermessiContext';
import { formatQuantity, formatCurrency } from '../../utils/formatters';
import { useBloccoRecordContesto } from '../../hooks/useBloccoRecordContesto';
import { API_DOCS } from './MovimentoDettaglioPage.api';

const MODULO = 'magazzino.movimenti';

type StatoMovimento = 'BOZZA' | 'CONFERMATO' | 'STORNATO';
type TipoCausale = 'CARICO' | 'SCARICO' | 'TRASFERIMENTO' | 'RETTIFICA';

const TIPO_LABEL: Record<TipoCausale, string> = {
  CARICO: 'Carico',
  SCARICO: 'Scarico',
  TRASFERIMENTO: 'Trasferimento',
  RETTIFICA: 'Rettifica',
};

interface Testata {
  id: number;
  societaId: number;
  numero: number | null;
  anno: number | null;
  data: string;
  stato: StatoMovimento;
  descrizione: string;
  note: string | null;
  numeroDocumentoEsterno: string | null;
  dataDocumentoEsterno: string | null;
  movimentoStornatoId: number | null;
  movimentoStornoId: number | null;
  causale: { id: number; codice: string; descrizione: string; tipo: TipoCausale; aggiornaCmp: boolean } | null;
  depositoOrigine: { id: number; nome: string; tipo: string } | null;
  depositoDestinazione: { id: number; nome: string; tipo: string } | null;
  nominativo: { id: number; codice: string; ragioneSociale: string | null; nome: string | null; cognome: string | null } | null;
  commessa: { id: number; numero: string; descrizione: string } | null;
}

interface Riga {
  id: number;
  ordine: number;
  articoloId: number;
  articolo: { id: number; codice: string; descrizioneBase: string } | null;
  quantita: number | string;
  prezzoUnitario: number | string;
  segno: number;
  note: string | null;
  // Carico da ordine fornitore: riga d'ordine di origine
  ordineFornitoreRiga: { id: number; ordinePos: number; ordineFornitore: { id: number; numero: string | null } | null } | null;
}

interface LogEntry {
  id: number;
  utenteName: string;
  azione: string;
  dettaglio: string | null;
  createdAt: string;
}

interface ArticoloLookup {
  id: number;
  codice: string;
  descrizioneBase: string;
  tipologia: string;
  umVendita: string | null;
  prezzoStandard: number | string | null;
  giacenzaDeposito: { quantita: number | string; cmp: number | string } | null;
}

interface FormRiga {
  rigaId: number | null;
  articolo: ArticoloLookup | null;
  quantita: string;
  prezzoUnitario: string;
  segno: 1 | -1;
  note: string;
}

const FORM_RIGA_VUOTA: FormRiga = {
  rigaId: null,
  articolo: null,
  quantita: '',
  prezzoUnitario: '',
  segno: 1,
  note: '',
};

export default function MovimentoDettaglioPage() {
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');
  const canDelete = can(MODULO, 'delete');
  const navigate = useNavigate();
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);

  const [testata, setTestata] = useState<Testata | null>(null);
  const [righe, setRighe] = useState<Riga[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [working, setWorking] = useState(false);

  // Blocco cambio società sulla scheda
  useBloccoRecordContesto({
    recordSocietaId: testata?.societaId,
    label: testata && testata.numero != null ? `Movimento ${testata.numero}/${testata.anno}` : 'Movimento di magazzino',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // @api GET /api/movimenti-magazzino/:id -> { success: true, data: Testata + righe, logs: [...] }
      const res = await apiFetch<{ success: boolean; data: Testata & { righe: Riga[] }; logs: LogEntry[] }>(`/api/movimenti-magazzino/${id}`);
      const { righe: rg, ...rest } = res.data;
      setTestata(rest as Testata);
      setRighe(rg ?? []);
      setLogs(res.logs ?? []);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento del movimento');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (Number.isFinite(id)) load(); }, [id]);

  // Stato testata
  const isBozza      = testata?.stato === 'BOZZA';
  const isConfermato = testata?.stato === 'CONFERMATO';
  const isStornato   = testata?.stato === 'STORNATO';
  const tipo         = testata?.causale?.tipo;

  // Totale valorizzato
  const totale = useMemo(() => {
    return righe.reduce((acc, r) => acc + Number(r.quantita) * Number(r.prezzoUnitario), 0);
  }, [righe]);

  // ───────── Modale modifica testata ─────────
  const [modalTestata, setModalTestata] = useState<null | {
    data: string;
    descrizione: string;
    numeroDocumentoEsterno: string;
    dataDocumentoEsterno: string;
    note: string;
  }>(null);
  const [testataError, setTestataError] = useState('');

  const apriModificaTestata = () => {
    if (!testata) return;
    setModalTestata({
      data: testata.data.slice(0, 10),
      descrizione: testata.descrizione,
      numeroDocumentoEsterno: testata.numeroDocumentoEsterno ?? '',
      dataDocumentoEsterno: testata.dataDocumentoEsterno ? testata.dataDocumentoEsterno.slice(0, 10) : '',
      note: testata.note ?? '',
    });
    setTestataError('');
  };
  const chiudiModificaTestata = () => { setModalTestata(null); setTestataError(''); };

  const salvaTestata = async () => {
    if (!modalTestata) return;
    if (!modalTestata.data) { setTestataError('Data obbligatoria'); return; }
    if (!modalTestata.descrizione.trim()) { setTestataError('Descrizione obbligatoria'); return; }
    setWorking(true); setTestataError('');
    try {
      // @api PUT /api/movimenti-magazzino/:id { data, descrizione, numeroDocumentoEsterno, dataDocumentoEsterno, note } -> { success: true, data: { id } }
      await apiFetch(`/api/movimenti-magazzino/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          data: modalTestata.data,
          descrizione: modalTestata.descrizione.trim(),
          numeroDocumentoEsterno: modalTestata.numeroDocumentoEsterno.trim() || null,
          dataDocumentoEsterno: modalTestata.dataDocumentoEsterno || null,
          note: modalTestata.note.trim() || null,
        }),
      });
      chiudiModificaTestata();
      load();
    } catch (e: any) {
      setTestataError(e.message || 'Errore nel salvataggio');
    } finally {
      setWorking(false);
    }
  };

  // ───────── Modale riga ─────────
  const [modalRiga, setModalRiga] = useState<FormRiga | null>(null);
  const [rigaError, setRigaError] = useState('');
  const [articoliRicerca, setArticoliRicerca] = useState('');
  const [articoliOpzioni, setArticoliOpzioni] = useState<ArticoloLookup[]>([]);
  const [articoliLoading, setArticoliLoading] = useState(false);
  const [articoliMenuOpen, setArticoliMenuOpen] = useState(false);

  const apriNuovaRiga = () => { setModalRiga({ ...FORM_RIGA_VUOTA }); setRigaError(''); setArticoliRicerca(''); setArticoliOpzioni([]); };
  const apriModificaRiga = (r: Riga) => {
    if (!r.articolo) return;
    setModalRiga({
      rigaId: r.id,
      articolo: {
        id: r.articolo.id, codice: r.articolo.codice, descrizioneBase: r.articolo.descrizioneBase,
        tipologia: '', umVendita: null, prezzoStandard: null, giacenzaDeposito: null,
      },
      quantita: String(r.quantita),
      prezzoUnitario: String(r.prezzoUnitario),
      segno: (Number(r.segno) === -1 ? -1 : 1),
      note: r.note ?? '',
    });
    setRigaError('');
  };
  const chiudiRiga = () => { setModalRiga(null); setRigaError(''); setArticoliMenuOpen(false); };

  // Ricerca articoli (debounce semplificato — fetch on every change)
  useEffect(() => {
    if (!modalRiga || modalRiga.articolo) return;
    if (articoliRicerca.trim().length < 1) { setArticoliOpzioni([]); return; }
    const handle = setTimeout(async () => {
      if (!testata) return;
      setArticoliLoading(true);
      try {
        const depForGiacenza = tipo === 'CARICO' ? testata.depositoDestinazione?.id
          : (tipo === 'SCARICO' || tipo === 'TRASFERIMENTO' || tipo === 'RETTIFICA') ? testata.depositoOrigine?.id
          : null;
        const qs = new URLSearchParams({ societaId: String(testata.societaId), q: articoliRicerca.trim() });
        if (depForGiacenza) qs.set('depositoId', String(depForGiacenza));
        // @api GET /api/movimenti-magazzino/lookup/articoli?societaId=&q=&depositoId= -> { success: true, data: [{ id, codice, descrizioneBase, tipologia, umVendita, prezzoStandard, giacenzaDeposito }] }
        const res = await apiFetch<{ success: boolean; data: ArticoloLookup[] }>(`/api/movimenti-magazzino/lookup/articoli?${qs.toString()}`);
        setArticoliOpzioni(res.data ?? []);
        setArticoliMenuOpen(true);
      } catch {
        setArticoliOpzioni([]);
      } finally {
        setArticoliLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articoliRicerca, modalRiga?.articolo]);

  // Suggerisci prezzo unitario quando si seleziona un articolo
  const selezionaArticolo = (a: ArticoloLookup) => {
    setModalRiga(m => {
      if (!m) return m;
      let prezzoSuggerito = '';
      if (tipo === 'CARICO') {
        prezzoSuggerito = a.prezzoStandard != null ? String(a.prezzoStandard) : '';
      } else if (a.giacenzaDeposito) {
        prezzoSuggerito = String(a.giacenzaDeposito.cmp);
      }
      return { ...m, articolo: a, prezzoUnitario: m.prezzoUnitario || prezzoSuggerito };
    });
    setArticoliMenuOpen(false);
  };

  const rimuoviArticoloSelezionato = () => {
    setModalRiga(m => m && { ...m, articolo: null, prezzoUnitario: '' });
    setArticoliRicerca('');
  };

  const salvaRiga = async () => {
    if (!modalRiga) return;
    if (!modalRiga.articolo) { setRigaError('Seleziona un articolo'); return; }
    const qta = Number(modalRiga.quantita);
    if (!Number.isFinite(qta) || qta <= 0) { setRigaError('Quantità deve essere > 0'); return; }
    const prezzo = Number(modalRiga.prezzoUnitario || 0);
    if (!Number.isFinite(prezzo) || prezzo < 0) { setRigaError('Prezzo non valido'); return; }
    if (tipo === 'CARICO' && testata?.causale?.aggiornaCmp && prezzo <= 0) {
      setRigaError('CARICO con aggiornaCmp: prezzo unitario > 0 obbligatorio'); return;
    }

    setWorking(true); setRigaError('');
    try {
      const payload: any = {
        articoloId: modalRiga.articolo.id,
        quantita: qta,
        prezzoUnitario: prezzo,
        note: modalRiga.note.trim() || null,
      };
      if (tipo === 'RETTIFICA') payload.segno = modalRiga.segno;

      if (modalRiga.rigaId === null) {
        // @api POST /api/movimenti-magazzino/:id/righe { articoloId, quantita, prezzoUnitario, segno, note } -> { success: true, data: { id, ordine } }
        await apiFetch(`/api/movimenti-magazzino/${id}/righe`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      } else {
        // @api PUT /api/movimenti-magazzino/:id/righe/:rigaId { quantita, prezzoUnitario, segno, note } -> { success: true }
        await apiFetch(`/api/movimenti-magazzino/${id}/righe/${modalRiga.rigaId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      }
      chiudiRiga();
      load();
    } catch (e: any) {
      setRigaError(e.message || 'Errore nel salvataggio della riga');
    } finally {
      setWorking(false);
    }
  };

  const eliminaRiga = async (r: Riga) => {
    if (!window.confirm(`Eliminare la riga ${r.ordine} (${r.articolo?.codice})?`)) return;
    setWorking(true); setActionError('');
    try {
      // @api DELETE /api/movimenti-magazzino/:id/righe/:rigaId -> { success: true }
      await apiFetch(`/api/movimenti-magazzino/${id}/righe/${r.id}`, { method: 'DELETE' });
      load();
    } catch (e: any) {
      setActionError(e.message || 'Errore nell\'eliminazione');
    } finally {
      setWorking(false);
    }
  };

  // ───────── Azioni testata ─────────

  const conferma = async () => {
    if (!testata) return;
    if (righe.length === 0) { setActionError('Aggiungi almeno una riga prima di confermare'); return; }
    if (!window.confirm('Confermare il movimento?\n\nVerrà assegnato un numero progressivo e le giacenze verranno aggiornate. Questa operazione è irreversibile (ma puoi sempre stornare).')) return;
    setWorking(true); setActionError('');
    try {
      // @api POST /api/movimenti-magazzino/:id/conferma -> { success: true, data: { id, numero, anno } }
      await apiFetch(`/api/movimenti-magazzino/${id}/conferma`, { method: 'POST' });
      load();
    } catch (e: any) {
      setActionError(e.message || 'Errore nella conferma');
    } finally {
      setWorking(false);
    }
  };

  const [modalStorno, setModalStorno] = useState<null | { data: string; motivo: string }>(null);
  // Stampa del carico merce (solo causali di tipo CARICO): motore di stampa, modulo CARICO_MERCE
  const [modalStampa, setModalStampa] = useState(false);
  const [stornoError, setStornoError] = useState('');

  const apriStorno = () => {
    setModalStorno({ data: new Date().toISOString().slice(0, 10), motivo: '' });
    setStornoError('');
  };
  const chiudiStorno = () => { setModalStorno(null); setStornoError(''); };
  const eseguiStorno = async () => {
    if (!modalStorno) return;
    if (!modalStorno.data) { setStornoError('Data storno obbligatoria'); return; }
    setWorking(true); setStornoError('');
    try {
      // @api POST /api/movimenti-magazzino/:id/storna { data, motivo } -> { success: true, data: { id, numero, anno } }
      const res = await apiFetch<{ success: boolean; data: { id: number; numero: number; anno: number } }>(`/api/movimenti-magazzino/${id}/storna`, {
        method: 'POST',
        body: JSON.stringify({ data: modalStorno.data, motivo: modalStorno.motivo.trim() || null }),
      });
      chiudiStorno();
      navigate(`/magazzino/movimenti/${res.data.id}`);
    } catch (e: any) {
      setStornoError(e.message || 'Errore nello storno');
    } finally {
      setWorking(false);
    }
  };

  const elimina = async () => {
    if (!testata) return;
    const messaggio = isBozza
      ? 'Eliminare questo movimento in BOZZA?'
      : isConfermato
        ? 'Eliminare questo movimento CONFERMATO?\n\nLe giacenze verranno ripristinate (rollback). L\'operazione è definitiva.'
        : 'Eliminare questo movimento?';
    if (!window.confirm(messaggio)) return;
    setWorking(true); setActionError('');
    try {
      // @api DELETE /api/movimenti-magazzino/:id -> { success: true }
      await apiFetch(`/api/movimenti-magazzino/${id}`, { method: 'DELETE' });
      navigate('/magazzino/movimenti');
    } catch (e: any) {
      setActionError(e.message || 'Errore nell\'eliminazione');
      setWorking(false);
    }
  };

  // ─────────  Render ─────────

  if (loading) return <Layout><div className="bop-empty-state">Caricamento…</div></Layout>;
  if (error)   return <Layout><div className="bop-inline-error">{error}</div></Layout>;
  if (!testata) return <Layout><div className="bop-empty-state">Movimento non trovato</div></Layout>;

  const titolo = testata.numero != null
    ? `Movimento ${testata.numero}/${testata.anno}`
    : `Bozza movimento #${testata.id}`;

  const statoBadge = (
    <span className={`bop-badge ${
      isConfermato ? 'bop-badge--success'
      : isStornato  ? 'bop-badge--danger'
      : 'bop-badge--neutral'
    }`}>
      {testata.stato.charAt(0) + testata.stato.slice(1).toLowerCase()}
    </span>
  );

  return (
    <Layout>
      <BopBackLink to="/magazzino/movimenti" label="Torna ai movimenti" />
      <div className="bop-page-header">
        <div>
          <h2 className="bop-page-title">{titolo} &nbsp; {statoBadge}</h2>
          <p className="bop-text-muted">
            {testata.causale ? `${TIPO_LABEL[testata.causale.tipo]} — ${testata.causale.codice} ${testata.causale.descrizione}` : 'Causale mancante'}
          </p>
        </div>
        <div className="bop-flex">
          {tipo === 'CARICO' && (
            <button
              className="bop-btn-secondary"
              onClick={() => setModalStampa(true)}
              title="Anteprima, PDF o invio email del carico merce con il template di Impostazioni → Report"
            >
              Stampa / Invia
            </button>
          )}
          {isBozza && canEdit && (
            <button className="bop-btn-secondary" onClick={apriModificaTestata} disabled={working}>Modifica testata</button>
          )}
          {isBozza && canEdit && (
            <button className="bop-btn-primary" onClick={conferma} disabled={working || righe.length === 0}>Conferma</button>
          )}
          {isConfermato && canEdit && !testata.movimentoStornoId && (
            <button className="bop-btn-secondary" onClick={apriStorno} disabled={working}>Storna</button>
          )}
          {canDelete && !isStornato && (
            <button className="bop-btn-danger" onClick={elimina} disabled={working}>Elimina</button>
          )}
        </div>
      </div>

      {actionError && <div className="bop-inline-error">{actionError}</div>}

      {/* Banner storno collegato */}
      {testata.movimentoStornoId && (
        <div className="bop-inline-error">
          Questo movimento è stato stornato. <button className="bop-btn-secondary" onClick={() => navigate(`/magazzino/movimenti/${testata.movimentoStornoId}`)}>Vai al movimento di storno</button>
        </div>
      )}
      {testata.movimentoStornatoId && (
        <div className="bop-inline-error">
          Questo è un movimento di storno. <button className="bop-btn-secondary" onClick={() => navigate(`/magazzino/movimenti/${testata.movimentoStornatoId}`)}>Vai al movimento originale</button>
        </div>
      )}

      {/* Sezione TESTATA */}
      <div className="bop-card">
        <h3 className="bop-mt-0">Testata</h3>
        <div className="bop-form-row">
          <div className="bop-form-group">
            <label className="bop-label">Data movimento</label>
            <div>{new Date(testata.data).toLocaleDateString('it-IT')}</div>
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Causale</label>
            <div>{testata.causale ? `${testata.causale.codice} — ${testata.causale.descrizione}` : '—'}</div>
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Aggiorna CMP</label>
            <div>{testata.causale?.aggiornaCmp ? 'Sì' : 'No'}</div>
          </div>
        </div>
        <div className="bop-form-row">
          <div className="bop-form-group">
            <label className="bop-label">Deposito origine</label>
            <div>{testata.depositoOrigine ? `${testata.depositoOrigine.nome} (${testata.depositoOrigine.tipo})` : <span className="bop-text-muted">—</span>}</div>
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Deposito destinazione</label>
            <div>{testata.depositoDestinazione ? `${testata.depositoDestinazione.nome} (${testata.depositoDestinazione.tipo})` : <span className="bop-text-muted">—</span>}</div>
          </div>
        </div>
        <div className="bop-form-group">
          <label className="bop-label">Descrizione</label>
          <div>{testata.descrizione}</div>
        </div>
        <div className="bop-form-row">
          <div className="bop-form-group">
            <label className="bop-label">Numero documento esterno</label>
            <div>{testata.numeroDocumentoEsterno || <span className="bop-text-muted">—</span>}</div>
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Data documento esterno</label>
            <div>{testata.dataDocumentoEsterno ? new Date(testata.dataDocumentoEsterno).toLocaleDateString('it-IT') : <span className="bop-text-muted">—</span>}</div>
          </div>
        </div>
        {testata.note && (
          <div className="bop-form-group">
            <label className="bop-label">Note</label>
            <div>{testata.note}</div>
          </div>
        )}
      </div>

      {/* Sezione RIGHE */}
      <div className="bop-card">
        <div className="bop-flex-between">
          <h3 className="bop-mt-0">Righe</h3>
          {isBozza && canEdit && (
            <button className="bop-btn-primary" onClick={apriNuovaRiga} disabled={working}>+ Aggiungi riga</button>
          )}
        </div>
        {righe.length === 0 ? (
          <div className="bop-empty-state">
            {isBozza ? 'Nessuna riga. Clicca "+ Aggiungi riga" per inserire articoli.' : 'Nessuna riga.'}
          </div>
        ) : (
          <table className="bop-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Articolo</th>
                <th className="bop-text-right">Quantità</th>
                {tipo === 'RETTIFICA' && <th className="bop-text-center">Segno</th>}
                <th className="bop-text-right">Prezzo unit. (€)</th>
                <th className="bop-text-right">Valore (€)</th>
                <th>Note</th>
                {isBozza && canEdit && <th className="bop-text-right">Azioni</th>}
              </tr>
            </thead>
            <tbody>
              {righe.map(r => {
                const qta = Number(r.quantita);
                const prezzo = Number(r.prezzoUnitario);
                const valore = qta * prezzo;
                return (
                  <tr key={r.id}>
                    <td>{r.ordine}</td>
                    <td>
                      {r.articolo ? (
                        <>
                          <strong>{r.articolo.codice}</strong>
                          <div className="bop-text-muted">{r.articolo.descrizioneBase}</div>
                        </>
                      ) : <span className="bop-text-muted">—</span>}
                      {r.ordineFornitoreRiga?.ordineFornitore && (
                        <div className="bop-text-muted">
                          Ordine fornitore{' '}
                          <Link
                            to={`/commerciale/passivo/ordini/${r.ordineFornitoreRiga.ordineFornitore.id}`}
                            onClick={e => e.stopPropagation()}
                          >
                            {r.ordineFornitoreRiga.ordineFornitore.numero ?? `#${r.ordineFornitoreRiga.ordineFornitore.id}`}
                          </Link>
                          {' '}· riga {r.ordineFornitoreRiga.ordinePos}
                        </div>
                      )}
                    </td>
                    <td className="bop-text-right">{formatQuantity(qta)}</td>
                    {tipo === 'RETTIFICA' && (
                      <td className="bop-text-center">
                        <span className={`bop-badge ${r.segno === -1 ? 'bop-badge--danger' : 'bop-badge--success'}`}>
                          {r.segno === -1 ? '−' : '+'}
                        </span>
                      </td>
                    )}
                    <td className="bop-text-right">{formatCurrency(prezzo)}</td>
                    <td className="bop-text-right"><strong>{formatCurrency(valore)}</strong></td>
                    <td><span className="bop-text-muted">{r.note || ''}</span></td>
                    {isBozza && canEdit && (
                      <td className="bop-text-right">
                        <BopRowActions actions={[
                          { icon: <RowIcon.Edit />, label: 'Modifica', onClick: () => apriModificaRiga(r) },
                          { icon: <RowIcon.Trash />, label: 'Elimina', onClick: () => eliminaRiga(r), variant: 'danger' },
                        ]} />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={tipo === 'RETTIFICA' ? 5 : 4} className="bop-text-right"><strong>Totale valorizzato</strong></td>
                <td className="bop-text-right"><strong>{formatCurrency(totale)}</strong></td>
                <td colSpan={isBozza && canEdit ? 2 : 1}></td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* Sezione LOG */}
      <div className="bop-card">
        <h3 className="bop-mt-0">Attività</h3>
        {logs.length === 0 ? (
          <div className="bop-empty-state">Nessuna attività registrata.</div>
        ) : (
          <table className="bop-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Utente</th>
                <th>Azione</th>
                <th>Dettaglio</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(l => (
                <tr key={l.id}>
                  <td>{new Date(l.createdAt).toLocaleString('it-IT')}</td>
                  <td>{l.utenteName}</td>
                  <td>{l.azione}</td>
                  <td><span className="bop-text-muted">{l.dettaglio || ''}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* MODALI */}

      {modalTestata && (
        <Modal
          title="Modifica testata"
          onClose={chiudiModificaTestata}
          footer={
            <>
              <button className="bop-btn-secondary" onClick={chiudiModificaTestata}>Annulla</button>
              <button className="bop-btn-primary" onClick={salvaTestata} disabled={working}>{working ? 'Salvo…' : 'Salva'}</button>
            </>
          }
        >
          {testataError && <div className="bop-inline-error">{testataError}</div>}
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Data *</label>
              <input type="date" className="bop-input"
                value={modalTestata.data}
                onChange={e => setModalTestata(m => m && { ...m, data: e.target.value })} />
            </div>
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Descrizione *</label>
            <input className="bop-input"
              value={modalTestata.descrizione}
              onChange={e => setModalTestata(m => m && { ...m, descrizione: e.target.value })} />
          </div>
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Numero doc. esterno</label>
              <input className="bop-input"
                value={modalTestata.numeroDocumentoEsterno}
                onChange={e => setModalTestata(m => m && { ...m, numeroDocumentoEsterno: e.target.value })} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Data doc. esterno</label>
              <input type="date" className="bop-input"
                value={modalTestata.dataDocumentoEsterno}
                onChange={e => setModalTestata(m => m && { ...m, dataDocumentoEsterno: e.target.value })} />
            </div>
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Note</label>
            <textarea className="bop-input" rows={2}
              value={modalTestata.note}
              onChange={e => setModalTestata(m => m && { ...m, note: e.target.value })} />
          </div>
        </Modal>
      )}

      {modalRiga && (
        <Modal
          title={modalRiga.rigaId === null ? 'Nuova riga' : 'Modifica riga'}
          onClose={chiudiRiga}
          footer={
            <>
              <button className="bop-btn-secondary" onClick={chiudiRiga}>Annulla</button>
              <button className="bop-btn-primary" onClick={salvaRiga} disabled={working || !modalRiga.articolo}>
                {working ? 'Salvo…' : 'Salva riga'}
              </button>
            </>
          }
        >
          {rigaError && <div className="bop-inline-error">{rigaError}</div>}

          {/* Selezione articolo */}
          <div className="bop-form-group">
            <label className="bop-label">Articolo *</label>
            {modalRiga.articolo ? (
              <div className="bop-flex-between">
                <div>
                  <strong>{modalRiga.articolo.codice}</strong> — {modalRiga.articolo.descrizioneBase}
                  {modalRiga.articolo.giacenzaDeposito && (
                    <div className="bop-text-muted">
                      Giacenza: {formatQuantity(Number(modalRiga.articolo.giacenzaDeposito.quantita))}
                      {' · CMP '}{formatCurrency(Number(modalRiga.articolo.giacenzaDeposito.cmp))}
                    </div>
                  )}
                </div>
                {modalRiga.rigaId === null && (
                  <button className="bop-btn-secondary" onClick={rimuoviArticoloSelezionato}>Cambia</button>
                )}
              </div>
            ) : (
              <>
                <input
                  className="bop-input"
                  placeholder="Cerca articolo per codice o descrizione…"
                  value={articoliRicerca}
                  onChange={e => setArticoliRicerca(e.target.value)}
                  onFocus={() => setArticoliMenuOpen(true)}
                  autoFocus
                />
                {articoliMenuOpen && articoliOpzioni.length > 0 && (
                  <div className="bop-card">
                    {articoliOpzioni.map(a => (
                      <div key={a.id} className="bop-table--clickable" onClick={() => selezionaArticolo(a)}
                        role="button" style={{ padding: '6px 8px', borderBottom: '1px solid var(--bop-color-border)', cursor: 'pointer' }}>
                        <strong>{a.codice}</strong> — {a.descrizioneBase}
                        {a.giacenzaDeposito && (
                          <span className="bop-text-muted">
                            {' · Giacenza '}{formatQuantity(Number(a.giacenzaDeposito.quantita))}
                            {' · CMP '}{formatCurrency(Number(a.giacenzaDeposito.cmp))}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {articoliLoading && <div className="bop-text-muted">Cerco…</div>}
              </>
            )}
          </div>

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Quantità *</label>
              <input
                type="number"
                step="0.001"
                min="0"
                className="bop-input"
                value={modalRiga.quantita}
                onChange={e => setModalRiga(m => m && { ...m, quantita: e.target.value })}
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">
                Prezzo unitario (€)
                {tipo === 'CARICO' && testata.causale?.aggiornaCmp && ' *'}
              </label>
              <input
                type="number"
                step="0.00001"
                min="0"
                className="bop-input"
                value={modalRiga.prezzoUnitario}
                onChange={e => setModalRiga(m => m && { ...m, prezzoUnitario: e.target.value })}
                placeholder={tipo === 'SCARICO' || tipo === 'TRASFERIMENTO' ? 'Verrà sostituito col CMP alla conferma' : ''}
              />
              {tipo === 'SCARICO' || tipo === 'TRASFERIMENTO' ? (
                <small className="bop-text-muted">Per SCARICO e TRASFERIMENTO il prezzo viene determinato alla conferma in base al CMP corrente (solo metodo CMP).</small>
              ) : null}
            </div>
          </div>

          {tipo === 'RETTIFICA' && (
            <div className="bop-form-group">
              <label className="bop-label">Segno rettifica *</label>
              <div className="bop-flex">
                <label className="bop-flex">
                  <input type="radio" checked={modalRiga.segno === 1}
                    onChange={() => setModalRiga(m => m && { ...m, segno: 1 })} />
                  <span>&nbsp;+ (aumenta giacenza)</span>
                </label>
                <span>&nbsp;&nbsp;</span>
                <label className="bop-flex">
                  <input type="radio" checked={modalRiga.segno === -1}
                    onChange={() => setModalRiga(m => m && { ...m, segno: -1 })} />
                  <span>&nbsp;− (diminuisce giacenza)</span>
                </label>
              </div>
            </div>
          )}

          <div className="bop-form-group">
            <label className="bop-label">Note riga</label>
            <input className="bop-input"
              value={modalRiga.note}
              onChange={e => setModalRiga(m => m && { ...m, note: e.target.value })} />
          </div>
        </Modal>
      )}

      {modalStorno && (
        <Modal
          title="Storna movimento"
          onClose={chiudiStorno}
          footer={
            <>
              <button className="bop-btn-secondary" onClick={chiudiStorno}>Annulla</button>
              <button className="bop-btn-primary" onClick={eseguiStorno} disabled={working}>{working ? 'Storno…' : 'Conferma storno'}</button>
            </>
          }
        >
          {stornoError && <div className="bop-inline-error">{stornoError}</div>}
          <p className="bop-text-muted">
            Verrà generato un movimento speculare in stato CONFERMATO che ripristina le giacenze.
            Questo movimento passerà in stato STORNATO.
          </p>
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Data storno *</label>
              <input type="date" className="bop-input"
                value={modalStorno.data}
                onChange={e => setModalStorno(m => m && { ...m, data: e.target.value })} />
            </div>
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Motivo (opzionale)</label>
            <textarea className="bop-input" rows={2}
              value={modalStorno.motivo}
              onChange={e => setModalStorno(m => m && { ...m, motivo: e.target.value })}
              placeholder="es. Errore di registrazione DDT 1234" />
          </div>
        </Modal>
      )}

      {modalStampa && (
        <PrintModal
          societaId={testata.societaId}
          entitaType="carico_merce"
          entitaId={testata.id}
          entitaLabel={titolo}
          onClose={() => setModalStampa(false)}
        />
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
